import { Expose, Type } from "class-transformer";
import { Contractor } from "@/models/api/Contractor";
import dayjs from "dayjs";
import { DEFAULT_DATE_FORMAT } from "@/static/dateFormats";
import { FileTmp } from "@/models/api/Files";
import { IsBoolean, IsDateString, IsEnum, IsOptional, IsString, ValidateNested } from "class-validator";
import { DOCUMENT_TYPES } from "@/static/documentTypes";
import { COST_DOCUMENT_STATUSES } from "@/static/costDocumentStatuses";

export class CostDocumentItem {
  @IsOptional()
  @Expose()
  id!: number | null;
  @Expose()
  index!: null | number;
  @Expose()
  name!: string;
  @Expose()
  quantity!: number;
  @Expose()
  netPriceInCurrency!: number;
  @Expose()
  grossInCurrency!: number;
  @IsOptional()
  @Expose()
  originalCostDocumentItemId!: number | null;

  constructor() {
    this.id = null;
    this.index = null;
    this.name = "";
    this.quantity = 1;
    this.netPriceInCurrency = 0;
    this.grossInCurrency = 0;
  }

  applyCalculation(calculation: CostDocumentItemCalculation): void {
    this.quantity = calculation.quantity;
    this.netPriceInCurrency = calculation.netPriceInCurrency;
    this.grossInCurrency = calculation.grossInCurrency;
  }
}

export class CostDocumentContractor {
  // shortName!: string;
  @IsOptional()
  @IsString()
  @Expose()
  name!: string;

  @IsOptional()
  @IsString()
  @Expose()
  surname!: string | null;

  @IsOptional()
  @IsString()
  @Expose()
  tin!: string | null;

  @IsOptional()
  @IsString()
  @Expose()
  nationalBusinessRegistryNumber!: string | null;

  @IsString()
  @Expose()
  country!: string;

  @IsOptional()
  @IsString()
  @Expose()
  city!: string | null;

  @IsOptional()
  @IsString()
  @Expose()
  postalCode!: string | null;

  @IsOptional()
  @IsString()
  @Expose()
  streetAndNumber!: string | null;

  @IsBoolean()
  @Expose()
  isIndividual!: boolean;

  @IsOptional()
  @IsString()
  @Expose()
  importedContractorId!: string | null;

  constructor() {
    this.name = "";
    this.surname = "";
    this.tin = "";
    this.nationalBusinessRegistryNumber = "";
    this.country = "";
    this.city = "";
    this.postalCode = "";
    this.streetAndNumber = "";
    this.isIndividual = true;
    this.importedContractorId = null;
  }

  static fromContractor(contractor: Contractor): CostDocumentContractor {
    const c = new CostDocumentContractor();
    c.name = contractor.name;
    c.importedContractorId = contractor.id;
    c.surname = contractor.surname;
    c.tin = contractor.tin;
    c.nationalBusinessRegistryNumber = contractor.nationalBusinessRegistryNumber;
    c.country = contractor.country;
    c.city = contractor.city;
    c.postalCode = contractor.postalCode;
    c.isIndividual = contractor.isIndividual;
    c.importedContractorId = contractor.id;
    c.streetAndNumber = contractor.streetAndNumber;
    return c;
  }
}

export class CostDocumentEdit {
  @Expose()
  sessionId!: string;
  @Expose()
  issueDate!: string;
  @Expose()
  saleDate!: string;
  @Expose()
  exchangeDate!: string;
  @Expose()
  receiptDate!: string;
  @Expose()
  paymentDate!: string;
  @Expose()
  documentNumber!: string;
  @Expose()
  @IsOptional()
  @IsString()
  ksefNumber!: string;
  @Expose()
  @IsOptional()
  @IsDateString()
  ksefAcquisitionDate!: string | null;
  @Expose()
  issuePlace!: string;
  @Expose()
  currency!: string;
  @Expose()
  description!: string;
  @Expose()
  amount!: number;
  @Expose()
  exchangeRate!: number;
  @Expose()
  documentTypeId!: number;

  @IsEnum(COST_DOCUMENT_STATUSES)
  @Expose()
  status!: COST_DOCUMENT_STATUSES;

  @Type(() => CostDocumentContractor)
  @ValidateNested()
  @Expose()
  contractor!: CostDocumentContractor;
  @Expose()
  formOfPaymentId!: number;
  @Type(() => CostDocumentItem)
  @ValidateNested()
  @Expose()
  costDocumentItems!: Array<CostDocumentItem>;

  //edit only

  @Expose()
  currentCostDocumentId!: string | null | undefined;

  @Type(() => FileTmp)
  @ValidateNested()
  @Expose()
  updateTemporaryFilesList!: Array<FileTmp>;

  constructor() {
    this.sessionId = "";
    this.issueDate = dayjs().format(DEFAULT_DATE_FORMAT);
    this.saleDate = dayjs().format(DEFAULT_DATE_FORMAT);
    this.exchangeDate = dayjs().format(DEFAULT_DATE_FORMAT);
    this.receiptDate = dayjs().format(DEFAULT_DATE_FORMAT);
    this.paymentDate = dayjs().format(DEFAULT_DATE_FORMAT);
    this.documentNumber = "";
    this.issuePlace = "";
    this.currency = "PLN";
    this.description = "";
    this.amount = 0;
    this.exchangeRate = 1;
    this.documentTypeId = DOCUMENT_TYPES.CostDocument;
    this.formOfPaymentId = 0;
    this.contractor = new CostDocumentContractor();
    this.updateTemporaryFilesList = [];
    this.costDocumentItems = [new CostDocumentItem()];
    this.status = COST_DOCUMENT_STATUSES.Incomplete;
    this.ksefAcquisitionDate = null;
    this.ksefNumber = "";
  }
}

export class CostDocumentItemCalculation {
  quantity!: number;
  netPriceInCurrency!: number;
  grossInCurrency!: number;
  constructor(item: CostDocumentItem) {
    this.quantity = item.quantity;
    this.netPriceInCurrency = item.netPriceInCurrency;
    this.grossInCurrency = item.grossInCurrency;
  }
}
